import { useState, useEffect, useImperativeHandle } from 'react';
import { DisplayFromFHIR, SimpleResourceFieldViewer, getStringFromFHIR } from './ResourceFunctions';
import { DataEntry, DatatypeSelector, MetadataPatternEdit, DisplayHowToCite } from './DataEntryFormFunctions';
import { DisplayClassifiers } from './MetadataPatternDisplay';
import { reorderRecommendationRatingContent } from './RecommendationJustificationFunctions';
import {
  sectionCodeCertaintyOfEvidence, sectionCodeDirectionOfRecommendation,  
  sectionCodeRatings, sectionCodeRatingSystem,
  sectionCodeStrengthOfRecommendation
} from './CodeSystemLookup';

const generateRecommendationRatingNarrative = (resource) => {
  let status = "generated";
  let innerDiv = "";
  try {
    if (resource.title) {
      innerDiv += "<p><b>Title: </b>" + resource.title + "</p><br/>";
    }
    innerDiv += "<p><b>Rating for Recommendation: </b>";
    if (resource.artifactReference) {
      innerDiv += getStringFromFHIR.Reference(resource.artifactReference) + "</p><br/>";
    } else if (resource.artifactCanonical) {
      innerDiv += resource.artifactCanonical + "</p><br/>";
    } else if (resource.artifactUri) {
      innerDiv += resource.artifactUri + "</p><br/>";
    } else {
      innerDiv += "[Reference to recommendation missing.]</p><br/>";
    }
    if (resource.content) {
      innerDiv += "<table><tr><th>Concept</th><th>Summary</th><th>Rating</th></tr>";
      for (const content of resource.content) {
        let type = getStringFromFHIR.CodeableConcept(content.type);
        let summary = content.summary;
        let rating = content.classifier?.map(classifier => getStringFromFHIR.CodeableConcept(classifier)).join("; ");
        if (summary || rating) {
          innerDiv += "<tr><td>" + type + "</td><td>" + summary + "</td><td>" + rating + "</td></tr>";
        }
        if (content.component) {
          for (const component of content.component) {
            let componenttype = getStringFromFHIR.CodeableConcept(component.type);
            let componentsummary = component.summary;
            let componentrating = component.classifier?.map(classifier => getStringFromFHIR.CodeableConcept(classifier)).join("; ");
            if (componentsummary || componentrating) {
              innerDiv += "<tr><td>" + componenttype + "</td><td>" + componentsummary + "</td><td>" + componentrating + "</td></tr>";
            }
          }
        }
      }
      innerDiv += "</table><br/>";
    }
    if (resource.certainty?.length > 0) { //this will not apply and this code can be removed
      innerDiv += resource.certainty.map((item) => {
        let certaintyType = getStringFromFHIR.CodeableConcept(item.type) || "untyped";
        let certaintyRating = getStringFromFHIR.CodeableConcept(item.rating) || "unrated";
        return "<p><b>Certainty of type " + certaintyType + ": </b>" + certaintyRating + "</p><br/>"
      }).join("");
    }
  } catch {
    innerDiv = "[Unable to generate Narrative Summary.]"
  }
  return { "status": status, "div": '<div xmlns=\"http://www.w3.org/1999/xhtml\">' + innerDiv + "</div>" };
}

const RecommendationRatingEdit = ({ fhirJson, formInputsStateRef, citationSummary, citationJson,
  classificationsArrayState, classificationsLoadedState }) => {

  let startingArtifactReferenceDatatype = 'none';
  if (fhirJson.artifactReference) { startingArtifactReferenceDatatype = 'Reference'; }
  if (fhirJson.artifactCanonical) { startingArtifactReferenceDatatype = 'canonical'; }
  if (fhirJson.artifactUri) { startingArtifactReferenceDatatype = 'uri'; }

  if (!fhirJson.content || !Array.isArray(fhirJson.content) || fhirJson.content.length === 0) {
    fhirJson.content = [
      {
        "type": sectionCodeRatingSystem
      },
      {
        "type": sectionCodeStrengthOfRecommendation
      },
      {
        "type": sectionCodeDirectionOfRecommendation
      },
      {
        "type": sectionCodeRatings
      },
      {
        "type": sectionCodeCertaintyOfEvidence
      }
    ]
  }

  const [resourceState, setResourceState] = useState({
    "resourceJson": fhirJson, "resourceType": "ArtifactAssessment", "id": fhirJson.id, "meta": fhirJson.meta, "implicitRules": fhirJson.implicitRules, "language": fhirJson.language, "text": fhirJson.text, "contained": fhirJson.contained, "extension": fhirJson.extension, "modifierExtension": fhirJson.modifierExtension,
    "url": fhirJson.url, "identifier": fhirJson.identifier, "version": fhirJson.version, "name": fhirJson.name, "title": fhirJson.title, "status": fhirJson.status, "experimental": fhirJson.experimental, "date": fhirJson.date, "publisher": fhirJson.publisher, "contact": fhirJson.contact, "description": fhirJson.description, "useContext": fhirJson.useContext, "jurisdiction": fhirJson.jurisdiction, "purpose": fhirJson.purpose, "usage": fhirJson.usage, "copyright": fhirJson.copyright, "copyrightLabel": fhirJson.copyrightLabel,
    "approvalDate": fhirJson.approvalDate, "lastReviewDate": fhirJson.lastReviewDate, "effectivePeriod": fhirJson.effectivePeriod, "topic": fhirJson.topic, "author": fhirJson.author, "editor": fhirJson.editor, "reviewer": fhirJson.reviewer, "endorser": fhirJson.endorser, "relatesTo": fhirJson.relatesTo || fhirJson.relatedArtifact,
    "artifactReference": fhirJson.artifactReference, "artifactCanonical": fhirJson.artifactCanonical, "artifactUri": fhirJson.artifactUri,
    "content": fhirJson.content, "workflowStatus": fhirJson.workflowStatus, "disposition": fhirJson.disposition,
    "citeAs": fhirJson.citeAs || fhirJson.citeAsMarkdown || fhirJson.citeAsReference?.display
  });
  const [artifactReferenceEditState, setArtifactReferenceEditState] = useState(false);
  const [artifactReferenceDatatypeState, setArtifactReferenceDatatypeState] = useState(startingArtifactReferenceDatatype);
  const [reorderedContentState, setReorderedContentState] = useState(false);

  useImperativeHandle(formInputsStateRef, () => ({
    recommendationRatingState: resourceState
  }), [resourceState]);

  useEffect((() => {
    if (artifactReferenceDatatypeState === 'uri') {
      setResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactCanonical': null } });
    }
    if (artifactReferenceDatatypeState === 'Reference') {
      setResourceState(prevState => { return { ...prevState, 'artifactUri': null, 'artifactCanonical': null } });
    }
    if (artifactReferenceDatatypeState === 'canonical') {
      setResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactUri': null } });
    }
  }), [artifactReferenceDatatypeState]);

  useEffect(() => {
    if (Array.isArray(fhirJson.content) && !reorderedContentState) {
      let reorderedContent = reorderRecommendationRatingContent(fhirJson.content);
      if (JSON.stringify(reorderedContent) !== JSON.stringify(fhirJson.content)) {
        alert("The ArtifactAssessment.content JSON was reordered to match the RecommendationRating Profile.");
      }
      setResourceState(prevState => { return { ...prevState, 'content': reorderedContent } });
      setReorderedContentState(true);
    }
  }, [fhirJson]);

  return <div style={{ marginTop: "12px" }}>
    <h3 id="summary">Summary</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype='string' elementName='title' fieldLabel='Title'
        startingValue={resourceState.title} setResourceState={setResourceState} />
      <div><b>The Recommendation Being Rated: </b>
        <DisplayFromFHIR reference={resourceState.artifactReference} />
        <DisplayFromFHIR uri={resourceState.artifactUri} />
        <span className={"unselectable"} style={{ marginLeft: "24px", cursor: "pointer" }} onClick={() => { setArtifactReferenceEditState(!artifactReferenceEditState) }}>
          <i>Change the Recommendation Resource Being Rated</i>&nbsp;&nbsp;
          {artifactReferenceEditState ? <>▼</> : <>►</>}
        </span>
      </div>
      {artifactReferenceEditState &&
        <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
          <DatatypeSelector elementXName='artifact[x]' allowedDatatypes={['Reference', 'canonical', 'uri']}
            datatypeState={artifactReferenceDatatypeState} setDatatypeState={setArtifactReferenceDatatypeState} />
          {(artifactReferenceDatatypeState === 'uri') &&
            <DataEntry datatype='uri' elementName='artifactUri'
              fieldLabel='The Resource Being Rated (by URL)' startingValue={resourceState.artifactUri || null}
              setResourceState={setResourceState} />}
          {(artifactReferenceDatatypeState === 'Reference') &&
            <DataEntry datatype='Reference' elementName='artifactReference' fieldLabel='The Resource Being Rated'
              startingValue={resourceState.artifactReference || null} setResourceState={setResourceState} />}
          {(artifactReferenceDatatypeState === 'canonical') &&
            <DataEntry datatype='uri' elementName='artifactCanonical' fieldLabel='The Resource Being Rated (by Canonical URI)'
              startingValue={resourceState.artifactCanonical || null} setResourceState={setResourceState} />}
          <br />
        </div>
      }
      <h4>Narrative Summary</h4>
      <div style={{ marginLeft: "24px" }}>
        <DataEntry datatype='Narrative' elementName='text' fieldLabel='Narrative Summary'
          generatedNarrative={generateRecommendationRatingNarrative(resourceState)}
          startingValue={resourceState.text} setResourceState={setResourceState} />
      </div>
    </div>
    <h3 id="rating-system">Rating System</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype="RecommendationJustificationComponent" elementName='content[0]'
        fieldLabel='Rating System'
        typeValueSet={[
          { system: "https://fevir.net/resources/CodeSystem/179423", code: "rating-system", display: "Rating System" }
        ]} typeSpecificValueSets={{
          "rating-system": [
            {
              system: "http://hl7.org/fhir/uv/ebm/CodeSystem/evidence-rating-system-classifier-codesystem",
              code: "GRADE", display: "GRADE"
            },
            {
              system: "http://hl7.org/fhir/uv/ebm/CodeSystem/evidence-rating-system-classifier-codesystem",
              code: "SIGN", display: "SIGN"
            },
            {
              system: "http://hl7.org/fhir/uv/ebm/CodeSystem/evidence-rating-system-classifier-codesystem",
              code: "AWMF", display: "AWMF"
            }
          ]
        }}
        startingValue={resourceState.content[0] || null} setResourceState={setResourceState}
        dataEntryStyle={'Rating System'} />
    </div>
    <h3 id="strength-of-recommendation">Strength of Recommendation</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype="RecommendationJustificationComponent" elementName='content[1]'
        fieldLabel='Strength of Recommendation'
        typeValueSet={[
          { system: "https://fevir.net/resources/CodeSystem/179423", code: "strength-of-recommendation", display: "Strength of Recommendation" }
        ]} typeSpecificValueSets={{
          "strength-of-recommendation": [
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2001", display: "Strong expectation" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2002", display: "Weak expectation" }
          ]
        }}
        startingValue={resourceState.content[1] || null} setResourceState={setResourceState}
        dataEntryStyle={'Strength of Recommendation'} />
    </div>
    <br />
    <h3 id="direction-of-recommendation">Direction of Recommendation</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype="RecommendationJustificationComponent" elementName='content[2]'
        fieldLabel='Direction of Recommendation'
        typeValueSet={[
          { system: "https://fevir.net/resources/CodeSystem/179423", code: "direction-of-recommendation", display: "Direction of Recommendation" }
        ]} typeSpecificValueSets={{
          "direction-of-recommendation": [
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2021", display: "Favors intervention" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2023", display: "Does not favor intervention or comparison" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2025", display: "Favors comparison" }
          ]
        }}
        startingValue={resourceState.content[2] || null} setResourceState={setResourceState}
        dataEntryStyle={'Direction of Recommendation'} />
    </div>
    <br />
    <h3 id="other-ratings">Other Ratings</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype="RecommendationJustificationComponent" elementName='content[3]'
        fieldLabel='Other Ratings'
        typeValueSet={[
          { system: "https://fevir.net/resources/CodeSystem/179423", code: "ratings", display: "Ratings" }
        ]}
        startingValue={resourceState.content[3] || null} setResourceState={setResourceState}
        dataEntryStyle={'Other Ratings'} />
    </div>
    <br />
    <h3 id="certainty-of-evidence">Certainty of Evidence</h3>
    <div style={{ marginLeft: "24px" }}>
      <DataEntry datatype="RecommendationJustificationComponent" elementName='content[4]'
        fieldLabel='Certainty of Evidence'
        typeValueSet={[
          { system: "https://fevir.net/resources/CodeSystem/179423", code: "certainty-of-evidence", display: "Certainty of Evidence" }
        ]} typeSpecificValueSets={{
          "certainty-of-evidence": [
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2003", display: "High certainty" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2004", display: "Moderate certainty" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2005", display: "Low certainty" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006", display: "Very low certainty" },
            { system: "https://fevir.net/resources/CodeSystem/27834", code: "RJCS-2006.0", display: "No evidence" }]
        }}
        startingValue={resourceState.content[4] || null} setResourceState={setResourceState}
        dataEntryStyle={'Certainty of Evidence'} />
    </div>
    <div>
      <h3 id="how-to-cite">How to Cite</h3>
      <div style={{ marginLeft: "24px" }}>
        <DisplayHowToCite citationSummary={citationSummary}
          citationJson={citationJson} />
      </div>
      <h3 id="metadata">Metadata</h3>
      <div style={{ marginLeft: "24px" }}>
        <MetadataPatternEdit resourceState={resourceState} setResourceState={setResourceState} />
      </div>
      <h3 id="classifiers">Classifiers</h3>
      <div style={{ marginLeft: "24px" }}>
        <div>
          <p>Add Classifiers:</p>
          <DataEntry asArray={true} datatype='Classification' elementName='newClassifications'
            fieldLabel='Classification' startingValue={resourceState.newClassifications} setResourceState={setResourceState} />
        </div>
        {(classificationsArrayState) && <div>
          <p>Existing Classifiers:</p>
          {classificationsLoadedState ?
            <DisplayClassifiers classificationsArray={classificationsArrayState} />
            :
            <><img style={{ height: "22px" }} src="/spinner.gif" alt="Loading" /> Classifiers being loaded...</>
          }
        </div>}
      </div>
      <h3 id="json-outline">JSON Outline</h3>
      <SimpleResourceFieldViewer resource={fhirJson} parentElement={""} />
      <br /><br />
    </div>
  </div>
};

export { RecommendationRatingEdit, generateRecommendationRatingNarrative };